/**
 * Filtering for the history timeline.
 *
 * Section and free-text matching run against the *rendered* form of an entry,
 * so what the reader searches for is what the reader sees: searching "Godišnji
 * odmor" finds entries whose fields are labelled that way, rather than entries
 * whose stored path happens to contain "holiday".
 */

import dayjs from 'dayjs';
import { groupHistoryChanges } from './groupHistoryChanges';

/** Period choices, in days. `all` keeps everything. */
export const HISTORY_PERIODS = Object.freeze([
  { value: 'all', labelKey: 'ui.periodAll' },
  { value: '7', labelKey: 'ui.period7' },
  { value: '30', labelKey: 'ui.period30' },
  { value: '90', labelKey: 'ui.period90' },
  { value: '365', labelKey: 'ui.period365' },
]);

export const DEFAULT_HISTORY_FILTERS = Object.freeze({
  period: 'all',
  user: 'all',
  section: 'all',
  query: '',
});

/**
 * Applies the filter bar to a timeline.
 *
 * @param {Array} events - Timeline entries from the history service.
 * @param {Object} filters - Current filter state.
 * @param {string} modelName - Model the record belongs to.
 * @param {Object} options - `{ t, locale }`, where `t` is bound to `history`.
 * @returns {Array} The entries that pass every active filter.
 */
export const applyHistoryFilters = (events, filters, modelName, options) => {
  const { period, user, section, query } = filters;
  const needle = String(query ?? '').trim().toLowerCase();

  const cutoff = period === 'all' ? null : dayjs().subtract(Number(period), 'day');

  return (events || []).filter((event) => {
    if (cutoff && dayjs(event.timestamp).isBefore(cutoff)) return false;

    if (user !== 'all') {
      const eventUser = event.user?._id ? String(event.user._id) : 'system';
      if (eventUser !== user) return false;
    }

    const needsGrouping = section !== 'all' || needle;
    if (!needsGrouping) return true;

    // A snapshot carries no change lines, so it can only survive a filter that
    // is not asking about a change.
    if (event.type === 'snapshot') return section === 'all' && !needle;

    const grouped = groupHistoryChanges(event.changes, modelName, options);

    if (section !== 'all' && !grouped.sections.some((s) => s.key === section)) {
      return false;
    }

    if (!needle) return true;

    return grouped.sections.some((entry) => {
      if (entry.label.toLowerCase().includes(needle)) return true;

      const allLines = [
        ...entry.lines,
        ...entry.arrays.flatMap((group) => group.items.flatMap((i) => i.lines)),
        ...entry.references.flatMap((group) => group.lines),
      ];

      return allLines.some(
        (line) =>
          line.label.toLowerCase().includes(needle) ||
          String(line.from).toLowerCase().includes(needle) ||
          String(line.to).toLowerCase().includes(needle) ||
          line.path.toLowerCase().includes(needle)
      );
    });
  });
};
